import React from 'react';
import { TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { InfoHint } from '@/components/tutorial/InfoHint';
import { reviewDesk } from '@/design-system';
import type { TrialStatus } from '../../../context/TrialContext';
import type { SubscriptionFlags } from '../planBilling';
import { formatDate, formatTrialRemaining, planDisplayName, usagePercent } from '../profileFormat';
import { profileWebStyles } from '../profileStyles';
import type { Translate, UserProfile } from '../profileTypes';
import { UsageMeter } from './UsageMeter';

interface SubscriptionCardProps {
  profile: UserProfile;
  flags: SubscriptionFlags;
  trialStatus: TrialStatus | null;
  isCompact: boolean;
  isPhone: boolean;
  formatCurrency: (amount?: number | null, currency?: string | null) => string;
  purchasedCredits: number;
  resumeLoading: boolean;
  onOpenChangePlan: () => void;
  onManageSubscription: () => void;
  onChoosePlan: () => void;
  onResumeSubscription: () => void;
  onOpenCancelSubscription: () => void;
  onBuyCredits: () => void;
  t: Translate;
}

/** Plan and trial status with billing actions, usage meters and credit balance. */
export function SubscriptionCard({
  profile,
  flags,
  trialStatus,
  isCompact,
  isPhone,
  formatCurrency,
  purchasedCredits,
  resumeLoading,
  onOpenChangePlan,
  onManageSubscription,
  onChoosePlan,
  onResumeSubscription,
  onOpenCancelSubscription,
  onBuyCredits,
  t,
}: SubscriptionCardProps) {
  const {
    canManagePlan,
    isPaidSubscriptionActive,
    isPaused,
    isTrialActive,
    isCardBackedTrial,
    shouldResubscribe,
    hasStripeBilling,
    canCancelBilling,
    canChangePaidPlan,
    shouldChoosePlan,
  } = flags;
  const planName = planDisplayName(t, profile.subscription.plan_tier);
  const monthlyTranscriptUsed = profile.usage.monthly_transcripts_used || 0;
  const monthlyTranscriptLimit = profile.usage.monthly_transcripts_limit || 0;
  const transcriptPercent = usagePercent(monthlyTranscriptUsed, monthlyTranscriptLimit);
  const dailyChatUsed = profile.usage.daily_chats_used || 0;
  const dailyChatLimit = profile.usage.daily_chats_limit || 0;
  const dailyChatPercent = usagePercent(dailyChatUsed, dailyChatLimit);
  const subscriptionCancelScheduled = Boolean(profile.subscription.cancel_at_period_end);
  const trialLabel = isCardBackedTrial
    ? t('profile.trialCardLabel')
    : t('profile.trialNoCardLabel');
  const trialDeadline = trialStatus?.expires_at
    ? `${t('profile.trialEndsOn')} ${formatDate(trialStatus.expires_at)} · ${formatTrialRemaining(t, trialStatus.hours_remaining)}`
    : t('profile.trialActive');
  const trialNotice = isCardBackedTrial
    ? t(
        'profile.trialCardNotice',
      )
    : t(
        'profile.trialNoCardNotice',
      );

  return (
    <View
      testID="profile_plan_credits"
      style={[profileWebStyles.subscriptionCard, isCompact && profileWebStyles.subscriptionCardCompact]}
    >
      <View
        style={[
          profileWebStyles.planBlock,
          isCompact && profileWebStyles.planBlockCompact,
          isPhone && profileWebStyles.planBlockPhone,
        ]}
      >
        <View style={profileWebStyles.bigIcon}>
          <MaterialIcons name="workspace-premium" size={31} color={reviewDesk.accent} />
        </View>
        <View style={profileWebStyles.planCopy}>
          <Text style={profileWebStyles.planLabel}>{t('profile.webPlanTitle')}</Text>
          <Text style={profileWebStyles.planTitle}>{planName}</Text>
          {isTrialActive ? (
            <>
              <View style={profileWebStyles.trialBadge}>
                <Text style={profileWebStyles.trialBadgeText}>{trialLabel}</Text>
              </View>
              <Text style={profileWebStyles.planMeta}>{trialDeadline}</Text>
              <Text style={profileWebStyles.trialNotice}>{trialNotice}</Text>
              {isCardBackedTrial && profile.subscription.amount ? (
                <Text style={profileWebStyles.planMeta}>
                  {t('profile.priceAfterTrial')
                    .replace('{{price}}', formatCurrency(profile.subscription.amount, profile.subscription.currency))}
                </Text>
              ) : null}
            </>
          ) : (
            <Text style={profileWebStyles.planMeta}>
              {isPaidSubscriptionActive ? t('profile.active') : t('profile.inactive')}
              {profile.subscription.amount ? ` · ${formatCurrency(profile.subscription.amount, profile.subscription.currency)}` : ''}
            </Text>
          )}
          {isPaused && profile.subscription.paused_from && profile.subscription.paused_until ? (
            <Text testID="subscription-paused-window" style={profileWebStyles.planMeta}>
              {t('profile.pausedFromUntil', {
                from: formatDate(profile.subscription.paused_from),
                until: formatDate(profile.subscription.paused_until),
              })}
            </Text>
          ) : null}
          {canManagePlan && (
            <View
              testID="profile_plan_actions"
              style={[profileWebStyles.planButtonRow, isPhone && profileWebStyles.planButtonRowPhone]}
            >
              {canChangePaidPlan && (
                <TouchableOpacity
                  style={[
                    profileWebStyles.smallOutlineButton,
                    isPhone && profileWebStyles.smallOutlineButtonPhone,
                  ]}
                  onPress={onOpenChangePlan}
                >
                  <Text style={profileWebStyles.smallOutlineText}>{t('profile.webManagePlan')}</Text>
                </TouchableOpacity>
              )}
              {hasStripeBilling && !canChangePaidPlan && (
                <TouchableOpacity
                  style={[
                    profileWebStyles.smallOutlineButton,
                    isPhone && profileWebStyles.smallOutlineButtonPhone,
                  ]}
                  onPress={onManageSubscription}
                >
                  <Text style={profileWebStyles.smallOutlineText}>{t('profile.webBillingPortalTitle')}</Text>
                </TouchableOpacity>
              )}
              {shouldResubscribe && (
                <TouchableOpacity
                  style={[
                    profileWebStyles.primaryButton,
                    { marginTop: 12, marginBottom: 0 },
                    isPhone && profileWebStyles.primaryButtonPhone,
                  ]}
                  onPress={onChoosePlan}
                >
                  <MaterialIcons name="workspace-premium" size={18} color="#FFFFFF" />
                  <Text style={profileWebStyles.primaryButtonText}>{t('profile.resubscribe')}</Text>
                </TouchableOpacity>
              )}
              {shouldChoosePlan && (
                <TouchableOpacity
                  style={[
                    profileWebStyles.smallOutlineButton,
                    isPhone && profileWebStyles.smallOutlineButtonPhone,
                  ]}
                  onPress={onChoosePlan}
                >
                  <Text style={profileWebStyles.smallOutlineText}>{t('profile.choosePlan')}</Text>
                </TouchableOpacity>
              )}
              {hasStripeBilling && (subscriptionCancelScheduled || isPaused) ? (
                <TouchableOpacity
                  testID="resume-subscription-button"
                  style={[
                    profileWebStyles.smallOutlineButton,
                    isPhone && profileWebStyles.smallOutlineButtonPhone,
                  ]}
                  onPress={onResumeSubscription}
                  disabled={resumeLoading}
                  accessibilityRole="button"
                >
                  <Text style={profileWebStyles.smallOutlineText}>
                    {isCardBackedTrial
                      ? t('profile.resumeTrialRenewal')
                      : t('profile.resumeSubscription')}
                  </Text>
                </TouchableOpacity>
              ) : null}
              {canCancelBilling ? (
                <TouchableOpacity
                  testID="cancel-subscription-button"
                  style={[
                    profileWebStyles.smallOutlineButton,
                    profileWebStyles.smallDangerButton,
                    isPhone && profileWebStyles.smallOutlineButtonPhone,
                  ]}
                  onPress={onOpenCancelSubscription}
                >
                  <Text style={profileWebStyles.smallDangerText}>
                    {isCardBackedTrial
                      ? t('profile.cancelTrial')
                      : t('profile.webCancelSubscription')}
                  </Text>
                </TouchableOpacity>
              ) : null}
            </View>
          )}
        </View>
      </View>

      <View
        testID="profile_usage_meters"
        style={[profileWebStyles.usageBlock, isCompact && profileWebStyles.usageBlockCompact]}
      >
        <UsageMeter
          label={isTrialActive
            ? t('profile.trialAnalyses')
            : t('profile.webMonthlyTranscripts')}
          value={`${monthlyTranscriptUsed} / ${monthlyTranscriptLimit || '-'}`}
          percent={transcriptPercent}
          icon="description"
          testID="profile_usage_analyses"
          color={reviewDesk.accent}
        />
        <UsageMeter
          label={isTrialActive
            ? t('profile.trialChats')
            : t('profile.webDailyChat')}
          value={`${dailyChatUsed} / ${dailyChatLimit || '-'}`}
          percent={dailyChatPercent}
          icon="chat-bubble-outline"
          testID="profile_usage_chats"
          color={reviewDesk.amber}
        />
      </View>

      <View style={[profileWebStyles.creditBlock, isCompact && profileWebStyles.creditBlockCompact]}>
        <View style={profileWebStyles.bigIcon}>
          <MaterialIcons name="credit-card" size={28} color={reviewDesk.accent} />
        </View>
        <Text style={profileWebStyles.planLabel}>{t('profile.webCreditBalance')}</Text>
        <Text style={profileWebStyles.planTitle}>{purchasedCredits}</Text>
        <Text style={profileWebStyles.planMeta}>{t('profile.webCreditHint')}</Text>
        <InfoHint id="credits_minutes" />
        <TouchableOpacity
          style={[
            profileWebStyles.smallOutlineButton,
            isPhone && profileWebStyles.smallOutlineButtonPhone,
          ]}
          onPress={onBuyCredits}
        >
          <Text style={profileWebStyles.smallOutlineText}>{t('profile.webBuyCredits')}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}
